import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { X, RotateCw, RotateCcw, FlipHorizontal2, ZoomIn, Check } from 'lucide-react';
import { getCroppedImg } from '../lib/cropImage';

// Lets a member zoom, crop (square) and rotate an image before saving it.
export default function CropModal({ src, onCancel, onConfirm }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flipX, setFlipX] = useState(false);
  const [areaPixels, setAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_area, areaPx) => setAreaPixels(areaPx), []);

  const confirm = async () => {
    if (!areaPixels) return;
    setBusy(true);
    try {
      const dataUrl = await getCroppedImg(src, areaPixels, rotation, { flipX });
      await onConfirm(dataUrl);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden" data-testid="crop-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
          <h3 className="font-title text-lg" style={{ color: 'var(--maroon)' }}>Adjust your photo</h3>
          <button onClick={onCancel} disabled={busy} data-testid="crop-cancel"><X size={20} /></button>
        </div>

        <div className="relative w-full" style={{ height: 300, background: '#222' }}>
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            transform={`translate(${crop.x}px, ${crop.y}px) rotate(${rotation}deg) scale(${zoom * (flipX ? -1 : 1)}, ${zoom})`}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center gap-2">
            <ZoomIn size={16} style={{ color: 'var(--olive)' }} />
            <input type="range" min={1} max={3} step={0.01} value={zoom} data-testid="crop-zoom"
              onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-[var(--maroon)]" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <RotateCw size={16} style={{ color: 'var(--olive)' }} />
            <input type="range" min={0} max={360} step={1} value={rotation} data-testid="crop-rotate"
              onChange={(e) => setRotation(Number(e.target.value))} className="flex-1 accent-[var(--maroon)]" />
            <button onClick={() => setRotation((r) => (r + 270) % 360)} data-testid="crop-rotate-left" title="Rotate left 90°"
              className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-title" style={{ background: 'white', border: '1px solid rgba(0,0,0,0.15)' }}>
              <RotateCcw size={13} />
            </button>
            <button onClick={() => setRotation((r) => (r + 90) % 360)} data-testid="crop-rotate-90" title="Rotate right 90°"
              className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-title" style={{ background: 'white', border: '1px solid rgba(0,0,0,0.15)' }}>
              <RotateCw size={13} />
            </button>
            <button onClick={() => setFlipX((f) => !f)} data-testid="crop-flip-h" title="Flip horizontally"
              className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-title" style={flipX ? { background: 'var(--maroon)', color: 'var(--cream)' } : { background: 'white', border: '1px solid rgba(0,0,0,0.15)' }}>
              <FlipHorizontal2 size={13} />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
          <button onClick={onCancel} disabled={busy} className="px-4 py-2 rounded text-sm font-title" style={{ background: 'white', border: '1px solid rgba(0,0,0,0.15)' }}>Cancel</button>
          <button onClick={confirm} disabled={busy || !areaPixels} data-testid="crop-confirm"
            className="flex items-center gap-2 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.7 : 1 }}>
            <Check size={15} /> {busy ? 'Saving…' : 'Set as photo'}
          </button>
        </div>
      </div>
    </div>
  );
}
